export interface StatusConfig {
  name: string;
  color: string;
  badgeColors: {
    bg: string;
    dot: string;
  };
}

export interface DriverStatusConfig {
  label: string;
  color: string;
  isPulse?: boolean;
}

export const ORDER_STATUS_CONFIG: Record<string, StatusConfig> = {
  pending: {
    name: "En Cola",
    color: "#FBBF24", // Amarillo
    badgeColors: {
      bg: "bg-yellow-100 text-yellow-800",
      dot: "text-yellow-400",
    },
  },
  accepted: {
    name: "Asignada",
    color: "#6366F1", // Indigo
    badgeColors: {
      bg: "bg-indigo-100 text-indigo-800",
      dot: "text-indigo-400",
    },
  },
  driver_at_branch: {
    name: "Conductor en Sucursal",
    color: "#8B5CF6", // Violeta
    badgeColors: {
      bg: "bg-violet-100 text-violet-800",
      dot: "text-violet-400",
    },
  },

  sorted: {
    name: "Surtida",
    color: "#F59E42", // Naranja
    badgeColors: {
      bg: "bg-orange-100 text-orange-800",
      dot: "text-orange-400",
    },
  },
  in_transit: {
    name: "En tránsito",
    color: "#14B8A6", // Turquesa
    badgeColors: {
      bg: "bg-cyan-100 text-cyan-800",
      dot: "text-cyan-400",
    },
  },
  on_site: {
    name: "En sitio",
    color: "#22C55E", // Verde brillante
    badgeColors: {
      bg: "bg-lime-100 text-lime-800",
      dot: "text-lime-400",
    },
  },
  completed: {
    name: "Completada",
    color: "#10B981", // Verde esmeralda
    badgeColors: {
      bg: "bg-emerald-100 text-emerald-800",
      dot: "text-emerald-400",
    },
  },
  cancelled: {
    name: "Cancelada",
    color: "#EF4444", // Rojo fuerte
    badgeColors: {
      bg: "bg-rose-100 text-rose-800",
      dot: "text-rose-400",
    },
  },
};

export const DRIVER_STATUS = {
  AVAILABLE: "available",
  ORDER_PENDING: "order_pending",
  ORDER_ACCEPTED: "order_accepted",
  DRIVER_AT_BRANCH: "driver_at_branch",
  ON_DELIVERY: "on_delivery",
  ON_SITE: "on_site",
  ON_BREAK: "on_break",
  BUSY: "busy",
  OFFLINE: "offline",
  UNKNOWN: "unknown",
} as const;

export const DRIVER_STATUS_CONFIG: Record<string, DriverStatusConfig> = {
  [DRIVER_STATUS.AVAILABLE]: { label: "Disponible", color: "bg-green-500" },
  [DRIVER_STATUS.ORDER_PENDING]: {
    label: "Con orden(es) pendiente(s)",
    color: "bg-yellow-500",
    isPulse: true,
  },
  [DRIVER_STATUS.ORDER_ACCEPTED]: {
    label: "Con orden(es) aceptada(s)",
    color: "bg-blue-400",
  },
  [DRIVER_STATUS.DRIVER_AT_BRANCH]: {
    label: "En sucursal",
    color: "bg-blue-600",
  },
  [DRIVER_STATUS.ON_DELIVERY]: { label: "En ruta", color: "bg-purple-500" },
  [DRIVER_STATUS.ON_SITE]: { label: "En sitio", color: "bg-purple-700" },
  [DRIVER_STATUS.ON_BREAK]: { label: "En descanso", color: "bg-orange-500" },
  [DRIVER_STATUS.BUSY]: { label: "Ocupado", color: "bg-gray-400" },
  [DRIVER_STATUS.OFFLINE]: {
    label: "De regreso o turno finalizado",
    color: "bg-red-500",
  },
  [DRIVER_STATUS.UNKNOWN]: { label: "Desconocido", color: "bg-gray-300" },
};

// Función helper para obtener los datos del donut chart
export const getDonutData = (stateMetrics: any[]) => {
  return Object.entries(ORDER_STATUS_CONFIG).map(([status, config]) => ({
    name: config.name,
    value: stateMetrics.find((m) => m.status === status)?.total_orders || 0,
    color: config.color,
  }));
};

// Función helper para obtener la configuración de un estado
export const getStatusConfig = (status: string): StatusConfig | null => {
  return ORDER_STATUS_CONFIG[status] || null;
};

// Función helper para obtener la configuración de un estado de conductor
export const getDriverStatusConfig = (status: string): DriverStatusConfig => {
  return DRIVER_STATUS_CONFIG[status] || DRIVER_STATUS_CONFIG.unknown;
};

/**
 * A driver is considered connected only when they have an active work session
 * in the API. MQTT telemetry can enrich the operational status and location,
 * but it may belong to a stale session and cannot establish connectivity.
 *
 * This must be shared by the map and driver views: MQTT only delivers new
 * messages, so its absence is not proof that a working driver is offline.
 */
export const isDriverConnected = (
  driverWorking?: boolean,
  _realTimeStatus?: string,
): boolean => {
  return Boolean(driverWorking);
};

// Función para obtener el texto de estado de un conductor (usada en DriverResumen y Map)
export const getDriverStatusText = (
  status: string | undefined,
  isWorking: boolean,
): string => {
  // Si el estado existe en la configuración y no es uno de los estados genéricos de "desconexión"
  if (
    status &&
    DRIVER_STATUS_CONFIG[status] &&
    status !== "unknown" &&
    status !== "offline"
  ) {
    return DRIVER_STATUS_CONFIG[status].label;
  }

  // Fallback al estado de conexión/trabajo (Conectado/Desconectado)
  return isWorking ? "Conectado" : "Desconectado";
};

// Función para obtener configuración de estado con display y color (para HistoricalOrders)
export const getOrderStatusDisplay = (status: string) => {
  switch (status) {
    case "paid":
      return {
        display: "Pagado",
        color: "bg-green-100 text-green-700 border-green-300",
      };
    case "order_accepted":
      return {
        display: "Orden Asignada",
        color: "bg-sky-100 text-sky-700 border-sky-300",
      };
    case "completed":
      return {
        display: "Completado",
        color: "bg-green-100 text-green-700 border-green-300",
      };
    case "locked":
      return {
        display: "Bloqueado",
        color: "bg-red-100 text-red-700 border-red-300",
      };
    case "unlocked":
      return {
        display: "Desbloqueado",
        color: "bg-green-100 text-green-700 border-green-300",
      };
    case "pending":
      return {
        display: "Pendiente",
        color: "bg-yellow-100 text-yellow-700 border-yellow-300",
      };
    case "sorted":
      return {
        display: "Surtida",
        color: "bg-orange-100 text-orange-700 border-orange-300",
      };
    case "driver_at_branch":
      return {
        display: "Driver en Sucursal",
        color: "bg-blue-100 text-blue-700 border-blue-300",
      };
    case "accepted":
      return {
        display: "Asignada",
        color: "bg-sky-100 text-sky-700 border-sky-300",
      };
    case "in_transit":
      return {
        display: "En Tránsito",
        color: "bg-teal-100 text-teal-700 border-teal-300",
      };
    case "on_site":
      return {
        display: "En Sitio",
        color: "bg-green-100 text-green-700 border-green-300",
      };
    case "on_delivery":
      return {
        display: "En Ruta",
        color: "bg-green-100 text-green-700 border-green-300",
      };
    case "cancelled":
    case "canceled":
      return {
        display: "Cancelado",
        color: "bg-red-100 text-red-700 border-red-300",
      };
    default:
      return {
        display: status,
        color: "bg-gray-100 text-gray-700 border-gray-300",
      };
  }
};
